import type { Ref } from "vue";
import { KNOWN_ERROR } from "@lib/types/errors";
import { toast } from "@/ui-plus/sonner";
import {
    checkExplicitDeliveryteamOrderPayload,
    checkDeliveryteamDataBeforePlacingOrder,
} from "@lib/utils/checkBeforePlacingDeliveryteamOrder";
import { geocode } from "@/ui-plus/geolocation-selection-map/geocode";
import { checkGeocodeDistance, getAddress } from "@lib/utils/checkGeocodeDistance";
import type { TaxSettings } from "@lib/zod/taxSchema";
import type { ResType } from "@api-client/typeUtils";
import { storeOrdersApiClient } from "@api-client/store_orders";
import { courierRequestState_perOrderRef } from "./state";
import { loadandCheckDeliveryteamOrder, cachedStoreOrders } from "../refHelps";
import { dispatchCloseCourierDialog } from "./events";

type CourierType = NonNullable<
    ResType<typeof storeOrdersApiClient.nearby_couriers[":storeId"]["$post"]>["data"]["couriers"]
>[number];

type OrderState = (typeof courierRequestState_perOrderRef)["value"][string];

function courierOrderFinished(
    storeOrderId: string,
    deliveryteamOrderId: string,
) {
    dispatchCloseCourierDialog({
        storeOrderId,
        deliveryteamOrderId,
    });
    loadandCheckDeliveryteamOrder(storeOrderId, deliveryteamOrderId);
}

/** Full-screen fallback when the browser blocks `window.open`: plain HTML + payment link. */
function mountBlockedCourierPaymentOverlay(
    paymentSessionUrl: string,
    storeOrderId: string,
    newOrderId: string,
) {
    const root = document.createElement("div");
    root.setAttribute("role", "dialog");
    root.setAttribute("aria-modal", "true");
    // Radix dialog scroll-lock often sets pointer-events:none on body; layer must opt back in to receive clicks.
    root.style.cssText =
        "position:fixed;inset:0;z-index:2147482990;display:grid;place-items:center;background:rgba(15,23,42,0.45);padding:16px;font-family:system-ui,sans-serif;box-sizing:border-box;pointer-events:auto;";

    const panel = document.createElement("div");
    panel.style.cssText =
        "max-width:400px;width:100%;background:#fff;color:#0f172a;border-radius:8px;padding:20px;display:flex;flex-direction:column;gap:12px;box-sizing:border-box;pointer-events:auto;position:relative;z-index:1;";

    const p = document.createElement("p");
    p.style.cssText = "margin:0;font-size:14px;line-height:1.5;";
    p.textContent =
        "Your browser blocked the checkout window. Open payment in a new tab, then tap Done.";

    function openPaymentInNewTab() {
        const w = window.open(paymentSessionUrl, "_blank");
        if (w) {
            try {
                w.opener = null;
            } catch {
                /* ignore cross-origin */
            }
        }
    }

    const openBtn = document.createElement("button");
    openBtn.type = "button";
    openBtn.textContent = "Open payment page";
    openBtn.style.cssText =
        "display:inline-flex;justify-content:center;align-items:center;min-height:44px;padding:10px 16px;background:#2563eb;color:#fff;border-radius:6px;border:none;font-weight:600;font-size:14px;cursor:pointer;pointer-events:auto;";
    openBtn.onclick = () => openPaymentInNewTab();

    const linkHint = document.createElement("a");
    linkHint.href = paymentSessionUrl;
    linkHint.target = "_blank";
    linkHint.rel = "noopener noreferrer";
    linkHint.textContent = "Or open as a normal link";
    linkHint.style.cssText =
        "font-size:13px;color:#2563eb;text-decoration:underline;cursor:pointer;pointer-events:auto;";

    const row = document.createElement("div");
    row.style.cssText = "display:flex;flex-wrap:wrap;gap:8px;";
    const btnDone = document.createElement("button");
    btnDone.type = "button";
    btnDone.textContent = "Done";
    btnDone.style.cssText =
        "flex:1;min-width:120px;min-height:44px;padding:10px;font-weight:600;border-radius:6px;border:none;background:#2563eb;color:#fff;cursor:pointer;pointer-events:auto;";
    const btnClose = document.createElement("button");
    btnClose.type = "button";
    btnClose.textContent = "Close";
    btnClose.style.cssText =
        "flex:1;min-width:120px;min-height:44px;padding:10px;font-weight:600;border-radius:6px;border:1px solid #cbd5e1;background:#fff;cursor:pointer;pointer-events:auto;";

    function remove() {
        root.remove();
    }
    btnDone.onclick = () => {
        remove();
        courierOrderFinished(storeOrderId, newOrderId);
    };
    btnClose.onclick = remove;

    row.append(btnDone, btnClose);
    panel.append(p, openBtn, linkHint, row);
    root.append(panel);
    // Stay clickable above Radix dialogs (body scroll-lock / inert on siblings).
    root.inert = false;
    panel.inert = false;
    document.body.append(root);
}

function openCourierPaymentPopupWindow(paymentSessionUrl: string): Window | null {
    const w = window.innerWidth;
    const h = window.innerHeight;
    const left = window.screenX;
    const top = window.screenY;
    const features = `width=${w},height=${h},left=${left},top=${top},scrollbars=yes,resizable=yes`;
    return window.open(paymentSessionUrl, "courier_payment", features);
}

function startCourierPaymentPopupPoll(
    popup: Window,
    storeOrderId: string,
    newOrderId: string,
) {
    const poll = window.setInterval(() => {
        if (popup.closed) {
            window.clearInterval(poll);
            courierOrderFinished(storeOrderId, newOrderId);
        }
    }, 400);
}

export const placeStoreCourierOrder = async (
    isPlaceOrderLoading: Ref<boolean>,
    storeId: string,
    storeOrderId: string,
    courier: CourierType,
    state: OrderState,
    selectedPaymentMethodId: string | undefined,
) => {
    if (!storeId) {
        toast.error("Store ID not found");
        return;
    }
    if (!storeOrderId) {
        toast.error("Order ID not found");
        return;
    }

    isPlaceOrderLoading.value = true;
    try {
        const pickupAddress = state.pickupAddress;
        const deliveryAddress = state.deliveryAddress;
        const currentCart = state.deliveryteamOrderInfosType;

        const { currentCart: cart, pickupAddress: pickup, deliveryAddress: delivery } =
            checkExplicitDeliveryteamOrderPayload(
                currentCart,
                pickupAddress ?? undefined,
                deliveryAddress ?? undefined,
                selectedPaymentMethodId,
            );

        const mapResultDelivery = await geocode(getAddress(delivery), delivery.country ?? "");
        checkGeocodeDistance(mapResultDelivery.data as any, {
            lat: delivery.lat!,
            lng: delivery.lng!,
        }, "DELIVERY");
        const mapResultPickup = await geocode(getAddress(pickup), pickup.country ?? "");
        checkGeocodeDistance(mapResultPickup.data as any, {
            lat: pickup.lat!,
            lng: pickup.lng!,
        }, "PICKUP");

        if (cart.scheduled_at_ts && Date.now() > cart.scheduled_at_ts) {
            cart.scheduled_at_ts = undefined;
        }

        checkDeliveryteamDataBeforePlacingOrder(
            {
                name: courier.deliveryteamName,
                isActive: courier.isActive ?? true,
            },
            {
                isSuspended: Boolean(courier.isSuspended),
            },
            courier.paymentMethods!,
            courier.taxSettings as TaxSettings,
            courier.openHours!,
            selectedPaymentMethodId!,
            cart.scheduled_at_ts,
            courier.hasNegativeBalance ?? false,
        );
        // Checks above mirror iframe _placeDeliveryteamOrder; full delivery-team + geocode
        // rules also run server-side in generateDeliveryteamOrderData_Service.
    } catch (error) {
        if (error instanceof KNOWN_ERROR) {
            console.error(error);
            toast.error(error.message);
        } else {
            console.error(error);
            toast.error("An unknown error occurred");
        }
        isPlaceOrderLoading.value = false;
        return;
    }

    const shortOtp = cachedStoreOrders.value[storeOrderId].shortOtp;
    if (!shortOtp) {
        console.error("Short OTP not found");
        toast.error("Short OTP not found");
        return;
    }
    toast.info("Placing order...");
    const { data, error } = await (
        await storeOrdersApiClient[":storeId"].place_courier_order.$post({
            param: { storeId },
            json: {
                currentCart: state.deliveryteamOrderInfosType!,
                deliveryAddress: state.deliveryAddress!,
                pickupAddress: state.pickupAddress!,
                selectedPaymentMethodId: selectedPaymentMethodId!,
                courierId: courier.courierId,
                deliveryteamId: courier.deliveryteamId,
                storeOrderId,

                shortOtp: shortOtp,
                dropoff_seller_notes: state.dropoffSellerNotes,
                return_notes: state.returnNotes,
                undeliverable_action: state.undeliverableAction,
                manifest_items: state.manifestItems,
            },
        })
    ).json();

    if (error) {
        console.error(error);
        toast.error(error.message ?? 'An unknown error occurred');
        isPlaceOrderLoading.value = false;
    } else {
        isPlaceOrderLoading.value = false;
        console.log(data);
        if (data.paymentSessionUrl) {
            const popup = openCourierPaymentPopupWindow(data.paymentSessionUrl);
            if (!popup) {
                mountBlockedCourierPaymentOverlay(data.paymentSessionUrl, storeOrderId, data.newOrderId);
                return;
            }
            startCourierPaymentPopupPoll(popup, storeOrderId, data.newOrderId);
        } else {
            courierOrderFinished(storeOrderId, data.newOrderId);
        }
    }
};